import { useEffect, useRef } from 'react';
import { IsEmpty } from './checks';

const HistoryStateKey = 'modal_back_stack';
const handlerStack = [];
let globalHandlerId = 0;
let suppressedPopstateCount = 0;

const canUseBrowserHistory = () => typeof window !== 'undefined' && typeof window.history?.pushState === 'function';

const onPopState = () => {
  if (suppressedPopstateCount > 0) {
    suppressedPopstateCount -= 1;
    return;
  }

  // eslint-disable-next-line no-unused-expressions
  handlerStack.at(-1)?.onBack();
};

const registerBackHandler = (onBack) => {
  if (!canUseBrowserHistory()) {
    return null;
  }

  if (IsEmpty(handlerStack)) {
    window.addEventListener('popstate', onPopState);
  }

  // eslint-disable-next-line no-plusplus
  const id = ++globalHandlerId;
  handlerStack.push({ id, onBack });
  window.history.pushState({ [HistoryStateKey]: true, id }, '', window.location.href);

  return id;
};

const unregisterBackHandler = (id, { consumeHistoryEntry = true } = {}) => {
  if (!canUseBrowserHistory() || IsEmpty(id)) {
    return;
  }

  const index = handlerStack.findIndex((h) => h.id === id);
  const isTopMost = handlerStack.at(-1)?.id === id;

  if (index >= 0) {
    handlerStack.splice(index, 1);
  }

  if (IsEmpty(handlerStack)) {
    window.removeEventListener('popstate', onPopState);
  }

  if (consumeHistoryEntry && isTopMost) {
    suppressedPopstateCount += 1;
    window.history.back();
  }
};

const useCloseOnBack = ({ isOpen, onClose }) => {
  const handlerIdRef = useRef(null);
  const closedByBackRef = useRef(false);
  const latestOnCloseRef = useRef(onClose);
  latestOnCloseRef.current = onClose;

  const closeWithoutConsumingHistory = useRef(() => {
    if (IsEmpty(handlerIdRef.current)) {
      return;
    }

    unregisterBackHandler(handlerIdRef.current, { consumeHistoryEntry: false });
    handlerIdRef.current = null;
    closedByBackRef.current = false;
  }).current;

  useEffect(() => {
    if (isOpen && IsEmpty(handlerIdRef.current)) {
      handlerIdRef.current = registerBackHandler(() => {
        // Mark immediately so cleanup/effects do not consume history during fast unmounts.
        closedByBackRef.current = true;
        const closeResult = latestOnCloseRef.current?.();
        if (closeResult === false) {
          closedByBackRef.current = false;
          // Close was blocked, restore the history entry
          window.history.pushState(
            { [HistoryStateKey]: true, id: handlerIdRef.current },
            '',
            window.location.href,
          );
        }
      });
    } else if (!isOpen && !IsEmpty(handlerIdRef.current)) {
      unregisterBackHandler(handlerIdRef.current, { consumeHistoryEntry: !closedByBackRef.current });
      handlerIdRef.current = null;
      closedByBackRef.current = false;
    }
  }, [isOpen]);

  // Cleanup on unmount
  useEffect(() => () => {
    if (!IsEmpty(handlerIdRef.current)) {
      unregisterBackHandler(handlerIdRef.current, { consumeHistoryEntry: !closedByBackRef.current });
      handlerIdRef.current = null;
      closedByBackRef.current = false;
    }
  }, []);

  return { closeWithoutConsumingHistory };
};

export default useCloseOnBack;
